import { _sumBy } from '@shared/libs/lodash'
import { serializeFilter } from '@shared/utils/url'

/**
 * Groups the raw data returned by the dashboard api into the configured groups
 *
 * Used when we want multiple statuses to be displayed together
 * (i.e. "In Service", "Commercial" for PG&E are both "Completed")
 *
 * @param {Array} chartConfig The chart configuration (from the projects.dashboard.config site config)
 * @param {Array} data The chart data, returned by the dasboard api
 * @returns Grouped data
 */
function groupChartData (chartConfig, data) {
  return chartConfig.groups.map(group => ({
    label: group.label,
    values: group.values.map(val => ({ label: val, value: data[val] || 0 })),
    total: _sumBy(group.values, val => data[val] || 0),
    color: group.color,
    url: buildFilterUrl(group.label, chartConfig, true),
    // TODO: urlFilter
  }))
}

/**
 * Creates an AgChart chart options object with configuration and data
 *
 * @param {Object} chart The chart configuration (from the projects.dashboard.config site config)
 * @param {Function} onClick OnClick event handler for the chart
 * @returns Donut chart config & data in the AgChart format
 */
function buildChartOptions (chart, onClick) {
  return {
    title: {
      text: chart.title,
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: 18,
    },
    series: [
      {
        type: 'donut',
        angleKey: 'total',
        calloutLabelKey: 'label',
        calloutLabel: {
          enabled: false,
        },
        innerRadiusRatio: 0.3,
        strokes: ['white'],
        strokeWidth: 1,
        fills: chart.groups.map(group => group.color),
        highlightStyle: {
          item: {
            stroke: 'white',
          },
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.label}: ${datum.total.toLocaleString()}`,
            content: datum.values.map(val => `${val.label}: ${val.value.toLocaleString()}`).join('<br>'),
          }),
        },
        listeners: {
          nodeClick: onClick,
        },
      },
    ],
    legend: {
      enabled: false,
    },
  }
}

function onSliceClick (event) {
  window.location.href = event.datum.url
}

function buildFilterUrl (label, chartConfig) {
  const { primaryAggField, primaryAggFilterValues, secondaryAggField, agGridFilter } = chartConfig
  const group = chartConfig.groups.find(grp => grp.label === label)
  let filter = {}

  if (primaryAggField && primaryAggFilterValues) filter[primaryAggField] = { values: primaryAggFilterValues }
  if (secondaryAggField) filter[secondaryAggField] = { values: group.values }
  if (agGridFilter) filter = { ...filter, ...agGridFilter }

  const serializedFilter = serializeFilter(filter)

  return `/projects/?filter=${serializedFilter}`
}

//

const mergeValues = (dictValues, dataValues) => {
  const dict = dictValues.reduce((d, vals) => {
    d[vals.label] = vals.value
    return d
  }, {})

  dataValues.forEach(val => {
    if (val.label in dict) {
      dict[val.label] += val.value
    } else {
      dict[val.label] = val.value
    }
  })

  return Object.entries(dict).map(([label, value]) => ({ label, value }))
}

const mergeChartsDataDict = (charts) => charts.reduce((dict, chart) => {
  const data = chart.options.data

  data.forEach(d => {
    if (d.label in dict) {
      dict[d.label].total += d.total
      dict[d.label].values = mergeValues(dict[d.label].values, d.values)
    } else dict[d.label] = { ...d }
  })

  return dict
}, {})

//

function buildLegend (charts) {
  if (charts.length === 1) return [...charts[0].options.data]

  const mergedChartsDataDict = mergeChartsDataDict(charts)
  const mergedChartsData = Object.values(mergedChartsDataDict)

  mergedChartsData.forEach(data => {
    data.url = buildFilterUrl(data.label, charts[0])
  })

  return mergedChartsData
}

function buildChart (chartConfig, chartData) {
  const options = buildChartOptions(chartConfig, onSliceClick)
  const groupedChartData = groupChartData(chartConfig, chartData)

  return {
    ...chartConfig,
    options: {
      ...options,
      data: groupedChartData,
    },
  }
}

function buildChartBlock (block, data) {
  const charts = block.charts.map(chart => buildChart(chart, data[chart.id]))
  const legend = buildLegend(charts)

  return {
    ...block,
    charts,
    legend,
  }
}

export {
  buildChartBlock,
}
